/**
 * "Still there?" — shown to the player on turn once they have done nothing for a
 * minute, in a game with other people waiting. When it reaches zero they are taken out
 * of the game. Part of the layout, never over the hand. scope.md §6.4 and §8.2.
 */

import { useEffect, useState } from 'react';
import { useT } from '../i18n.js';

/** How close to the deadline the prompt appears. Matches IDLE_WARNING_MS on the server. */
export const WARNING_MS = 20_000;

export function secondsLeft(idleOutAt: number | null, now: number): number | null {
  if (idleOutAt === null) return null;
  return Math.max(0, Math.ceil((idleOutAt - now) / 1000));
}

export function StillThere({
  idleOutAt, onExtend,
}: { idleOutAt: number | null; onExtend: () => void }) {
  const t = useT();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (idleOutAt === null) return;
    const handle = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(handle);
  }, [idleOutAt]);

  const left = secondsLeft(idleOutAt, now);
  if (left === null || left * 1000 > WARNING_MS) return null;

  return (
    <div
      role="alert"
      data-testid="still-there"
      className="mx-3 mt-2 flex items-center gap-3 rounded-2xl bg-coral/20 px-3 py-2 ring-2 ring-coral"
    >
      <p className="flex-1 text-sm font-bold">
        {t('stillThere.question')}{' '}
        <span className="text-coral" data-testid="still-there-seconds">{t('seconds.short', { count: left })}</span>
        <span className="block text-[10px] font-normal text-bone/70">
          {t('stillThere.hint')}
        </span>
      </p>
      <button
        type="button"
        data-testid="extend-turn"
        onClick={onExtend}
        className="rounded-xl bg-sun px-4 text-base font-black text-ink active:scale-95"
      >
        {t('stillThere.more')}
      </button>
    </div>
  );
}
